import { calculateCapitalReturnLines, capitalReturnEntries } from '../../domain/lifecycleCalc';
import type { Random } from '../../lib/random';
import type { CapitalReturn, Holder, LedgerEntry, RedemptionLine } from '../types';
import { classById, entry, opening, type Draft } from './seedLedger';
import { FIDES_TENANT_ID, priceAt } from './seedReference';

const BRIDGING_AMOUNTS = [100_000, 150_000, 200_000, 250_000, 300_000, 500_000];

function allocateBridging(random: Random, holders: Holder[], drafts: Draft[]) {
  const fides = holders.filter(
    (holder) => holder.tenantId === FIDES_TENANT_ID && !holder.investorLoginId,
  );
  drafts.push(opening('h-bennett-ind', 'c-brg1-o', 150_000));
  drafts.push(opening('h-bennett-trust', 'c-brg2-o', 200_000));
  drafts.push(opening('h-mehta-co', 'c-brg2-o', 500_000));
  fides
    .slice(0, 12)
    .forEach((holder) =>
      drafts.push(opening(holder.id, 'c-brg1-o', random.pick(BRIDGING_AMOUNTS))),
    );
  fides
    .slice(12, 26)
    .forEach((holder) =>
      drafts.push(opening(holder.id, 'c-brg2-o', random.pick(BRIDGING_AMOUNTS))),
    );
}

function windUpBrg1(drafts: Draft[], holders: Holder[]): CapitalReturn {
  const lines = calculateCapitalReturnLines(
    'c-brg1-o',
    '2026-08-15',
    100,
    1.35,
    1,
    drafts as LedgerEntry[],
    holders,
  );
  const capitalReturn: CapitalReturn = {
    id: 'cr-brg1-final',
    fundId: 'f-brg1',
    classId: 'c-brg1-o',
    kind: 'FINAL',
    returnPct: 100,
    reason: 'Borrower repaid the Eastfield loan in full on 15 August 2026 (early).',
    recordDate: '2026-08-15',
    paymentDate: '2026-08-20',
    finalIncomeCentsPerUnit: 1.35,
    status: 'PAID',
    createdBy: 'Alex Morgan',
    approvedBy: 'Priya Nair',
    lines,
  };
  capitalReturnEntries(capitalReturn, 1, 'Alex Morgan').forEach((item, index) =>
    drafts.push({ ...item, id: `le-brg1-${index}`, postedAt: '2026-08-20T09:00:00' }),
  );
  return capitalReturn;
}

function pastRedemptions(holders: Holder[], drafts: Draft[]): RedemptionLine[] {
  const unitClass = classById('c-core-a');
  const price = priceAt(unitClass, '2026-09-30');
  const coreHolders = holders
    .filter((holder) => holder.tenantId === FIDES_TENANT_ID && !holder.investorLoginId)
    .filter((holder) =>
      drafts.some((item) => item.holderId === holder.id && item.classId === 'c-core-a'),
    )
    .slice(0, 2);
  return coreHolders.map((holder, index) => {
    const held = drafts
      .filter((item) => item.holderId === holder.id && item.classId === 'c-core-a')
      .reduce((total, item) => total + item.units, 0);
    const units = Math.round(held * 0.25);
    const amount = Math.round(units * price * 100) / 100;
    drafts.push({
      ...entry(
        holder.id,
        'c-core-a',
        'REDEMPTION',
        '2026-09-30',
        amount,
        'Redeemed at the 30 Sep window',
        'REDEMPTION SEP',
      ),
      id: `le-red-sep-${index}`,
      units: -units,
      unitPrice: price,
      eventId: 'rr-core-sep',
    });
    return {
      holderId: holder.id,
      requestId: `cr-red-sep-${index}`,
      unitsRequested: units,
      unitsRedeemed: units,
      amount,
    };
  });
}

export function seedLifecycleLedger(random: Random, holders: Holder[], drafts: Draft[]) {
  allocateBridging(random, holders, drafts);
  const capitalReturns = [windUpBrg1(drafts, holders)];
  return { capitalReturns, pastRedemptions: pastRedemptions(holders, drafts) };
}
